// Library methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import ConnectionForm from "src/pages/EmrConnections/ConnectionForm/ConnectionForm";

// Utils
import {
  ConnectionFormValues,
  FIXED_DIRECTIONS,
  buildConnectionConfig,
  connectionFormValuesFromConfig,
  mergeWithStoredConfig,
} from "src/utils/emr/connectionConfigHelper";

// Hooks
import { useUpdateEMRHubConnection } from "src/services/emr/hooks/emr-connections/useUpdateEMRHubConnection";

// Types
import {
  EMRCatalogDeviceType,
  EMRConnectionType,
} from "src/services/emr/types";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

type EditEMRConnectionModalType = {
  open: boolean;
  clinicId: string | undefined;
  connection: EMRConnectionType;
  device?: EMRCatalogDeviceType;
  sharedFolderPaths: Array<string> | undefined;
  onClose: () => void;
};

const EditEMRConnectionModal = ({
  open,
  clinicId,
  connection,
  device,
  sharedFolderPaths,
  onClose,
}: EditEMRConnectionModalType) => {
  const { t } = useTranslation();

  const [name, setName] = useState(connection.name);
  const [values, setValues] = useState<ConnectionFormValues>(() =>
    connectionFormValuesFromConfig(connection)
  );

  const { mutate, isPending } = useUpdateEMRHubConnection(clinicId);

  const sourceKind = connection.sourceKind;

  const examTypeOptions = useMemo(
    () => device?.examTypes ?? connection.config?.examTypes ?? [],
    [device, connection.config?.examTypes]
  );

  const configJson = useMemo(() => {
    const built = buildConnectionConfig(sourceKind, values);
    return built && mergeWithStoredConfig(sourceKind, connection.config, built);
  }, [sourceKind, values, connection.config]);

  const isDuplicatePath =
    sourceKind === "SharedFolder" &&
    Boolean(sharedFolderPaths?.includes(values.path)) &&
    values.path !== (connection.config?.path ?? "");

  const resetForm = useCallback(() => {
    setName(connection.name);
    setValues(connectionFormValuesFromConfig(connection));
  }, [connection]);

  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleValuesChange = useCallback(
    (changes: Partial<ConnectionFormValues>) =>
      setValues((previous) => ({ ...previous, ...changes })),
    []
  );

  const handleConfirm = useCallback(() => {
    if (!clinicId || configJson === null) return;

    mutate(
      {
        connectionId: connection.connectionId,
        clinicId,
        name,
        sourceKind,
        direction: FIXED_DIRECTIONS[sourceKind] ?? connection.direction,
        configJson,
        enabled: connection.enabled,
        deviceKey: connection.deviceKey ?? null,
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
      }
    );
  }, [
    connection.connectionId,
    connection.direction,
    connection.enabled,
    connection.deviceKey,
    clinicId,
    name,
    sourceKind,
    configJson,
    mutate,
    resetForm,
    onClose,
  ]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={`${t("Edit")} ${connection.name}`}
      maxWidth="sm"
      modalActions={
        <Grid sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            onClick={handleConfirm}
            disabled={
              !name.trim() ||
              isPending ||
              configJson === null ||
              isDuplicatePath
            }
          >
            {t("Save")}
          </Button>
        </Grid>
      }
    >
      <ConnectionForm
        idPrefix="edit-emr-hub-connection"
        name={name}
        onNameChange={setName}
        sourceKind={sourceKind}
        values={values}
        onValuesChange={handleValuesChange}
        isDuplicatePath={isDuplicatePath}
        examTypeOptions={examTypeOptions}
        showProfile={false}
      />
    </DefaultModal>
  );
};

export default EditEMRConnectionModal;
